import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Formik, Form, Field, useFormikContext } from "formik";
import {
  Text,
  Heading,
  Stack,
  Shelf,
  Box,
  TextLink,
  Button,
  Checkbox,
  Input,
  Banner,
  Icon,
} from "@chaser/components";
import * as Yup from "yup";
import get from "lodash/get";
import { CircleCheck, ShieldCheck } from "lucide-react";
import useSSORestrictedDomains from "../../../../hooks/useSSORestrictedDomains";
import LabeledFieldWithErrorLabel from "../../../../components/LabeledFieldWithErrorLabel/index.jsx";
import usePush from "../../../../hooks/usePush";
import useQueryParams from "../../../../hooks/useQueryParams";
import AuthenticationService from "../../../../services/authentication-service";
import AccountService from "../../../../services/account-service";
import { checkPasswordStrength } from "../../../../util/passwordStrength";
import { isBlockedEmailDomain } from "../../../../util/blockedEmailDomains";
import Logo from "../../../../components/Logo";
import GoogleButton from "../../../login/components/SSO/GoogleButton/index.jsx";
import IntuitButton from "../../../login/components/SSO/IntuitButton/index.jsx";
import XeroButton from "../../../login/components/SSO/XeroButton";
import MicrosoftButton from "../../../login/components/SSO/MicrosoftButton/index.jsx";
import { pendo } from "../../../../util/logging";
import style from "./SignupForm.module.scss";

const RESEND_COOLDOWN_SECONDS = 30;
const GENERIC_SIGNUP_ERROR = "Something went wrong. Try again later";

const getSignupErrorMessage = (error) =>
  get(error, "0.message", error?.message || GENERIC_SIGNUP_ERROR);

const validationSchema = Yup.object().shape({
  firstName: Yup.string().trim().required("First name is required").max(100),
  lastName: Yup.string().trim().required("Last name is required").max(100),
  email: Yup.string()
    .trim()
    .email("Enter a valid email")
    .required("Email is required")
    .max(100)
    .test(
      "no-blocked-domain",
      "Please use a work or personal email address",
      (value) => !isBlockedEmailDomain(value),
    ),
  password: Yup.string()
    .required("Password is required")
    .max(100)
    .test(
      "checkPasswordStrength",
      "Password too weak. Try making it longer, or adding uppercase letters, symbols or numbers.",
      checkPasswordStrength(3, 10),
    ),
  termsAccepted: Yup.boolean().oneOf(
    [true],
    "Please accept the terms to continue",
  ),
});

function SSOOnlyBanner() {
  return (
    <Banner
      intent="info"
      showCloseButton={false}
      icon={<ShieldCheck color="#3f84d6" size={18} />}
    >
      <Text as="span" size="small">
        Your organisation requires sign-in via SSO. Please use one of the
        options above.
      </Text>
    </Banner>
  );
}

function SignupFormFields({ loading, signupError, setSignupError }) {
  const { values, isValid, dirty, submitCount } = useFormikContext();
  const { isDomainRestricted } = useSSORestrictedDomains();
  const restricted = isDomainRestricted(values.email);

  return (
    <Stack alignItems="stretch" gap="small" className={style.authBodyStack}>
      {signupError && (
        <div className={style.signupErrorMessage}>
          <p className={style.signupErrorText}>{signupError}</p>
          <button
            type="button"
            className={style.signupErrorIcon}
            onClick={() => setSignupError("")}
          >
            <Icon name="close" size={12} color="Red" />
          </button>
        </div>
      )}

      <Stack
        justifyContent="center"
        alignItems="center"
        gap="xsmall"
        width="100%"
      >
        <GoogleButton
          label="Sign up with Google"
          onClick={() =>
            pendo.track("signup_sso_clicked", { provider: "google" })
          }
        />
        <IntuitButton
          label="Sign up with Intuit"
          onClick={() =>
            pendo.track("signup_sso_clicked", { provider: "intuit" })
          }
        />
        <MicrosoftButton
          label="Sign up with Microsoft"
          onClick={() =>
            pendo.track("signup_sso_clicked", { provider: "microsoft" })
          }
        />
        <XeroButton
          label="Sign up with Xero"
          onClick={() =>
            pendo.track("signup_sso_clicked", { provider: "xero" })
          }
        />
      </Stack>

      {!restricted && (
        <div className={style.authDivider}>
          <span>or sign up with email</span>
        </div>
      )}

      <Stack gap="xsmall" textAlign="left">
        {!restricted && (
          <Shelf gap="small" alignItems="flex-start" wrap="nowrap">
            <div style={{ flex: 1, minWidth: 0 }}>
              <LabeledFieldWithErrorLabel
                as={Input}
                icon="user"
                name="firstName"
                label="First name"
                placeholder="First name"
                width="100%"
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <LabeledFieldWithErrorLabel
                as={Input}
                name="lastName"
                label="Last name"
                placeholder="Last name"
                width="100%"
              />
            </div>
          </Shelf>
        )}
        <LabeledFieldWithErrorLabel
          as={Input}
          icon="email"
          name="email"
          type="email"
          label="Email"
          placeholder="Enter your email address"
          width="100%"
        />
        {!restricted && (
          <LabeledFieldWithErrorLabel
            as={Input}
            icon="lock"
            name="password"
            type="password"
            label="Password"
            autocomplete="off"
            placeholder="Create password"
            width="100%"
          />
        )}
      </Stack>

      {restricted ? (
        <SSOOnlyBanner />
      ) : (
        <>
          <Field name="termsAccepted" type="checkbox">
            {({ field, meta }) => (
              <Stack gap="xsmall" width="100%" textAlign="left">
                <Checkbox {...field} checked={field.value}>
                  I accept the{" "}
                  <a
                    href="https://www.chaserhq.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    terms of service
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://www.chaserhq.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    privacy policy
                  </a>
                  .
                </Checkbox>
                <Text color="Red" size="xsmall">
                  {submitCount > 0 ? meta.error : ""}
                </Text>
              </Stack>
            )}
          </Field>

          <Button
            loading={loading}
            disabled={!dirty || !isValid}
            type="submit"
            style={{ width: "100%" }}
            onClick={() => pendo.track("signup_email_submitted")}
          >
            Sign up
          </Button>
        </>
      )}

      <div className={style.formFooterDivider} />
      <Text className={style.footnoteText}>
        Already have an account?{" "}
        <TextLink as={Link} to="/login">
          Log in
        </TextLink>
      </Text>
    </Stack>
  );
}

const SignupForm = () => {
  const [signupError, setSignupError] = useState("");
  const [email, setEmail] = useState("");
  const [isComplete, setIsComplete] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(
    RESEND_COOLDOWN_SECONDS,
  );
  const [resent, setResent] = useState(false);
  const hasTrackedFreeTrialSignupRef = useRef(false);

  const { execute: executeRegister, loading: loadingRegister } = usePush(
    AuthenticationService.register,
    { errorToast: false },
  );
  const { execute: executeSetPassword, loading: loadingSetPassword } = usePush(
    AuthenticationService.setNewPassword,
  );
  const { execute: executeResendVerifyEmail } = usePush(
    AccountService.resendVerifyEmailEmail,
    {
      successToast: "Verification link sent",
      errorToast: "Failed to send verification link",
    },
  );

  const loading = loadingRegister || loadingSetPassword;

  // Sends a GTM dataLayer event so external analytics can track successful free-trial signups.
  const trackFreeTrialSignupSuccess = () => {
    if (hasTrackedFreeTrialSignupRef.current || typeof window === "undefined") {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "free_trial_signup",
      signup_status: "success",
    });

    hasTrackedFreeTrialSignupRef.current = true;
  };

  const handleSignupError = (error, actions) => {
    actions.setSubmitting(false);
    setSignupError(getSignupErrorMessage(error));
  };

  async function handleSignupSubmit(values, actions) {
    setSignupError("");

    const trimmedEmail = values.email.trim().toLowerCase();
    const firstName = values.firstName.trim();
    const lastName = values.lastName.trim();

    const [registerError, registerResponse] = await executeRegister({
      firstName,
      lastName,
      email: trimmedEmail,
      platform: "web",
    });

    if (registerError) {
      handleSignupError(registerError, actions);
      return;
    }

    const [setPasswordError] = await executeSetPassword(
      trimmedEmail,
      registerResponse.token,
      values.password,
    );

    if (setPasswordError) {
      handleSignupError(setPasswordError, actions);
      return;
    }

    actions.setSubmitting(false);
    trackFreeTrialSignupSuccess();
    setEmail(trimmedEmail);
    setIsComplete(true);
    setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
    setResent(false);
    actions.resetForm();
  }

  useEffect(() => {
    if (!isComplete || cooldownSeconds === 0) return;

    const id = setTimeout(() => {
      setCooldownSeconds((current) => Math.max(0, current - 1));
    }, 1000);

    return () => {
      clearTimeout(id);
    };
  }, [isComplete, cooldownSeconds]);

  async function resendVerificationLink() {
    if (!email || cooldownSeconds > 0) return;

    const [err] = await executeResendVerifyEmail(email);
    if (err) return;

    setResent(true);
    setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
  }

  if (isComplete) {
    return (
      <div className={style.signupPageFormContainer}>
        <div className={style.signupFormHead}>
          <Logo color="White" />
        </div>
        <Box py="medium" px={["small", "large"]}>
          <Stack gap="medium" alignItems="center" textAlign="center">
            <CircleCheck
              className={style.signupSuccessIcon}
              size={52}
              strokeWidth={2.2}
            />
            <Heading as="h2" className={style.authFormTitle}>
              Please verify your email address
            </Heading>
            <Text>
              Please check your inbox at{" "}
              <span className={style.signupSuccessEmail}>{email}</span> to
              verify your email address.
            </Text>

            <Text className={style.resendText}>
              {cooldownSeconds === 0 ? (
                <TextLink
                  as="button"
                  type="button"
                  onClick={resendVerificationLink}
                >
                  Resend link
                </TextLink>
              ) : (
                <span>Resend link in {cooldownSeconds}s</span>
              )}
              {resent && " (sent)"}
            </Text>
          </Stack>
        </Box>
      </div>
    );
  }

  const {
    firstname: firstNameParam = "",
    lastname: lastNameParam = "",
    email: emailParam = "",
  } = useQueryParams();

  return (
    <div className={style.signupPageFormContainer}>
      <Formik
        initialValues={{
          firstName: firstNameParam,
          lastName: lastNameParam,
          email: emailParam,
          password: "",
          termsAccepted: false,
        }}
        validationSchema={validationSchema}
        validateOnBlur={false}
        onSubmit={handleSignupSubmit}
      >
        <Form>
          <div className={style.signupFormHead}>
            <Logo color="White" />
          </div>
          <Box py="medium" px={["small", "large"]}>
            <Stack gap="medium" textAlign="center" width="100%">
              <Stack gap="small">
                <Heading as="h2" className={style.authFormTitle}>
                  Create an account
                </Heading>
                <Text size="small" className={style.footnoteText}>
                  Start your free trial. No credit card required.
                </Text>
              </Stack>
              <SignupFormFields
                loading={loading}
                signupError={signupError}
                setSignupError={setSignupError}
              />
            </Stack>
          </Box>
        </Form>
      </Formik>
    </div>
  );
};

export default SignupForm;
